import React, { useCallback, useEffect, useRef, useState } from 'react';
import './styles.css';
import { useAuthStore } from '../../../../../store/auth/auth';
import { LayoutType } from '../../../../../types/LayoutType';
import img from '../../../../../components/layouts/camera/Video.png';
import { Card } from 'antd';
import { FILE_PLAY_URL, PREVIEW_URL, VIDEO_PREVIEW_URL } from '../../../../../const/const';
import ButtonAddPlus from '../../../../../components/buttons/buttonAddPlus/ButtonAddPlus';
import Tag from '../../../../../utils/shared/components/Tags/Tag';
import { useLayoutsStore } from '../../../../../store/layout/useLayoutsStore';
import { VIDEO_FILE_PREVIEW } from '../../../../../api/file/FilePreview';

interface LayoutComponentProps {
    layout: LayoutType;
    handleAddLayout: (layout: LayoutType) => void;
}

const LayoutComponent: React.FC<LayoutComponentProps> = ({ layout, handleAddLayout }) => {
    const { SmartDVRToken, user } = useAuthStore();
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleItems, setVisibleItems] = useState(layout.devices.length);
    const [items, setItems] = useState(layout.devices);
    const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});

    // Константы для расчетов
    const ITEM_WIDTH = 258;
    const GAP = 8;
    const COUNTER_WIDTH = 72;
    const MIN_VISIBLE_ITEMS = 1;

    const calculateVisibleItems = useCallback(() => {
        if (!containerRef.current) return;

        const containerWidth = containerRef.current.clientWidth;
        let newVisibleItems = items.length;
        let needCounter = false;

        const totalItemsWidth = items.length * ITEM_WIDTH + (items.length - 1) * GAP;

        if (totalItemsWidth > containerWidth) {
            newVisibleItems = Math.floor((containerWidth + GAP) / (ITEM_WIDTH + GAP));

            if (newVisibleItems < items.length) {
                const availableWidth = containerWidth - COUNTER_WIDTH - GAP;
                newVisibleItems = Math.floor((availableWidth + GAP) / (ITEM_WIDTH + GAP));
                needCounter = items.length > newVisibleItems;
            }
        }

        setVisibleItems(Math.max(MIN_VISIBLE_ITEMS, newVisibleItems));
    }, [items]);

    useEffect(() => {
        setItems(layout.devices);
    }, [layout.devices]);

    useEffect(() => {
        calculateVisibleItems();
        const resizeObserver = new ResizeObserver(calculateVisibleItems);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, [calculateVisibleItems]);

    // Функция для загрузки медиа с авторизацией
    const fetchMedia = async (deviceUID: string) => {
        try {
            console.log(user);
            const response = await fetch(PREVIEW_URL(deviceUID), {
                headers: {
                    SmartDVRLogin: user?.login || '',
                    SmartDVRToken: SmartDVRToken,
                },
            });

            if (!response.ok) throw new Error('Failed to fetch media');

            const blob = await response.blob();
            return URL.createObjectURL(blob);
        } catch (error) {
            console.error('Error fetching media:', error);
            return img; // Fallback image
        }
    };

    // Загрузка медиа для всех устройств
    useEffect(() => {
        const loadMedia = async () => {
            const urls: Record<string, string> = {};
            for (const device of items) {
                urls[device.UID] = await fetchMedia(device.UID);
            }
            setMediaUrls(urls);
        };

        if (items.length > 0 && SmartDVRToken) {
            loadMedia();
        }

        return () => {
            Object.values(mediaUrls).forEach((url) => {
                if (url !== img) URL.revokeObjectURL(url);
            });
        };
    }, [items, SmartDVRToken]);

    const remainingCount = Math.max(0, items.length - visibleItems);
    const showCounter = remainingCount > 0;

    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        e.currentTarget.src = img;
    };

    return (
        <div className="containerLayout">
            <div className="propertiesLayout">
                <span className="nameLayout">
                    <div className="layoutComponentContainerTitle">
                        <span className="Name_Layout title large">{layout?.name}</span>
                        <ButtonAddPlus onClick={() => handleAddLayout(layout)} />
                    </div>

                    <span className="body medium layoutComponentText" style={{ marginBottom: 22 }}>
                        {layout.description}, {layout.viewType}
                    </span>

                    <div className="layoutComponentContainerFlex">
                        <Tag state="processing" border={true}>
                            {layout.devices.length} устройств
                        </Tag>
                        <Tag state="default" border={true}>
                            {layout.userName}
                        </Tag>
                    </div>
                </span>
            </div>

            <div ref={containerRef} className="containerLayoutDeviceImages">
                {items.slice(0, visibleItems).map((device) => (
                    <div key={device.ID} className="coverLayout">
                        <Card
                            cover={
                                <img
                                    src={mediaUrls[device.UID] || img}
                                    className="image imageSmall"
                                    alt=""
                                    onError={handleError}
                                />
                            }
                        />
                    </div>
                ))}

                {showCounter && <div className="remaining-counter">+{remainingCount}</div>}
            </div>
        </div>
    );
};

export default LayoutComponent;
